import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Plus, Save, Trash2, Eye, Lock } from "lucide-react";

const FORMATIONS = {
  "4-3-3": [
    { id: "GK", x: 50, y: 92 },
    { id: "LB", x: 15, y: 70 }, { id: "LCB", x: 38, y: 75 }, { id: "RCB", x: 62, y: 75 }, { id: "RB", x: 85, y: 70 },
    { id: "LCM", x: 30, y: 50 }, { id: "CM", x: 50, y: 52 }, { id: "RCM", x: 70, y: 50 },
    { id: "LW", x: 18, y: 25 }, { id: "ST", x: 50, y: 18 }, { id: "RW", x: 82, y: 25 },
  ],
  "4-2-3-1": [
    { id: "GK", x: 50, y: 92 },
    { id: "LB", x: 15, y: 70 }, { id: "LCB", x: 38, y: 75 }, { id: "RCB", x: 62, y: 75 }, { id: "RB", x: 85, y: 70 },
    { id: "LCDM", x: 38, y: 56 }, { id: "RCDM", x: 62, y: 56 },
    { id: "CAM", x: 50, y: 38 }, { id: "LM", x: 18, y: 32 }, { id: "RM", x: 82, y: 32 },
    { id: "ST", x: 50, y: 16 },
  ],
  "4-4-2": [
    { id: "GK", x: 50, y: 92 },
    { id: "LB", x: 15, y: 70 }, { id: "LCB", x: 38, y: 75 }, { id: "RCB", x: 62, y: 75 }, { id: "RB", x: 85, y: 70 },
    { id: "LM", x: 18, y: 48 }, { id: "LCM", x: 40, y: 50 }, { id: "RCM", x: 60, y: 50 }, { id: "RM", x: 82, y: 48 },
    { id: "LST", x: 38, y: 22 }, { id: "RST", x: 62, y: 22 },
  ],
  "3-5-2": [
    { id: "GK", x: 50, y: 92 },
    { id: "LCB", x: 28, y: 76 }, { id: "CB", x: 50, y: 80 }, { id: "RCB", x: 72, y: 76 },
    { id: "LM", x: 12, y: 48 }, { id: "LCM", x: 35, y: 52 }, { id: "CM", x: 50, y: 56 }, { id: "RCM", x: 65, y: 52 }, { id: "RM", x: 88, y: 48 },
    { id: "LST", x: 38, y: 22 }, { id: "RST", x: 62, y: 22 },
  ],
  "5-2-1-2": [
    { id: "GK", x: 50, y: 92 },
    { id: "LWB", x: 12, y: 68 }, { id: "LCB", x: 30, y: 78 }, { id: "CB", x: 50, y: 82 }, { id: "RCB", x: 70, y: 78 }, { id: "RWB", x: 88, y: 68 },
    { id: "LCM", x: 38, y: 52 }, { id: "RCM", x: 62, y: 52 },
    { id: "CAM", x: 50, y: 35 },
    { id: "LST", x: 38, y: 18 }, { id: "RST", x: 62, y: 18 },
  ],
};

const TEAM_INSTRUCTIONS = [
  { k: "mentality", l: "Mentality", opts: ["Ultra Defensive", "Defensive", "Balanced", "Attacking", "Ultra Attacking"] },
  { k: "build_up", l: "Build Up", opts: ["Slow Build Up", "Balanced", "Fast Build Up"] },
  { k: "chance_creation", l: "Chance Creation", opts: ["Possession", "Forward Runs", "Direct Passing"] },
  { k: "width_attack", l: "Width (Attack)", opts: ["Narrow", "Balanced", "Wide"] },
  { k: "depth_attack", l: "Depth (Attack)", opts: ["Deep", "Balanced", "High"] },
  { k: "def_style", l: "Defensive Style", opts: ["Drop Back", "Balanced", "Press On Possession Loss", "Constant Pressure"] },
  { k: "def_width", l: "Width (Defence)", opts: ["Narrow", "Balanced", "Wide"] },
  { k: "def_depth", l: "Depth (Defence)", opts: ["Deep", "Balanced", "High"] },
];

const PLAYER_INSTRUCTIONS_PER_ROLE = {
  attack: ["Stay Forward", "Get In Behind", "Stay Central", "Cut Inside", "Free Roam", "Target Man"],
  midfield: ["Stay Back", "Get Forward", "Cover Center", "Cover Wing", "Press Aggressively", "Conservative Interceptions"],
  defense: ["Stay Back", "Cover Center", "Cover Wing", "Step Up", "Aggressive Interceptions"],
};

function classifyRole(id) {
  if (id === "GK") return "gk";
  if (/(CB|LB|RB|WB)/.test(id)) return "defense";
  if (/(CDM|CM|CAM|LM|RM)/.test(id)) return "midfield";
  return "attack";
}

export default function Tactics() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/tactics", { params: { mine: true } }).then((r) => setItems(r.data)); }, []);
  return (
    <div className="space-y-6 fade-in" data-testid="tactics-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Tactical Builder</div>
          <h1 className="font-display text-4xl tracking-tight mt-2">Meine Taktiken</h1>
          <p className="text-white/60 text-sm mt-2">EA FC 26 Formationen, Spielerinstruktionen & Set-Pieces.</p>
        </div>
        <Link to="/tactics/new" className="btn-primary" data-testid="new-tactic-btn">
          <Plus className="w-4 h-4" /> Neue Taktik
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/40">
          Noch keine Taktiken angelegt.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <Link key={t.id} to={`/tactics/${t.id}`} className="card-solid p-5 hover:border-primary/40 transition-colors group" data-testid={`tactic-${t.id}`}>
              <div className="flex items-center gap-2 text-gradient">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t.formation}</span>
                {!t.is_public && <Lock className="w-3 h-3 text-white/40" />}
              </div>
              <div className="font-display text-xl mt-2 tracking-tight">{t.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">
                {t.players?.length || 0} Spieler · aktualisiert {new Date(t.updated_at).toLocaleDateString("de-DE")}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function TacticEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";

  const [name, setName] = useState("Meine Taktik");
  const [formation, setFormation] = useState("4-3-3");
  const [players, setPlayers] = useState(() => FORMATIONS["4-3-3"].map((p) => ({ ...p, name: "", role: classifyRole(p.id), instructions: [] })));
  const [instructions, setInstructions] = useState({});
  const [setPieces, setSetPieces] = useState({ corners_taker: "", free_kicks_taker: "", penalty_taker: "" });
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isNew) {
      api.get(`/tactics/${id}`).then((r) => {
        const t = r.data;
        setName(t.name);
        setFormation(t.formation);
        setPlayers(t.players);
        setInstructions(t.instructions || {});
        setSetPieces(t.set_pieces || {});
        setIsPublic(t.is_public);
      });
    }
  }, [id, isNew]);

  const changeFormation = (f) => {
    setFormation(f);
    const base = FORMATIONS[f];
    setPlayers(base.map((p) => {
      const existing = players.find((x) => x.id === p.id);
      return existing ? { ...existing, x: p.x, y: p.y } : { ...p, name: "", role: classifyRole(p.id), instructions: [] };
    }));
    setSelected(null);
  };

  const updatePlayer = (idx, patch) => {
    setPlayers((ps) => ps.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const togglePlayerInstruction = (idx, instr) => {
    setPlayers((ps) =>
      ps.map((p, i) => {
        if (i !== idx) return p;
        const set = new Set(p.instructions || []);
        if (set.has(instr)) set.delete(instr);
        else set.add(instr);
        return { ...p, instructions: Array.from(set) };
      })
    );
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const body = { name, formation, players, instructions, set_pieces: setPieces, is_public: isPublic };
      if (isNew) {
        const { data } = await api.post("/tactics", body);
        navigate(`/tactics/${data.id}`, { replace: true });
      } else {
        await api.put(`/tactics/${id}`, body);
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Taktik löschen?")) return;
    await api.delete(`/tactics/${id}`);
    navigate("/tactics");
  };

  return (
    <div className="space-y-6 fade-in" data-testid="tactic-editor-page">
      <Link to="/tactics" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-primary link-underline">← Taktiken</Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card-solid p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="label">Name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="tactic-name-input" />
            </div>
            <div>
              <label className="label">Formation</label>
              <select className="input" value={formation} onChange={(e) => changeFormation(e.target.value)} data-testid="tactic-formation-select">
                {Object.keys(FORMATIONS).map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
          </div>

          {/* Pitch */}
          <div className="relative aspect-[1/1.4] card-solid overflow-hidden" data-testid="tactic-pitch">
            <div className="absolute inset-0" style={{
              background: "linear-gradient(180deg, rgba(0,255,157,0.08) 0%, rgba(41,233,255,0.04) 50%, rgba(194,71,255,0.06) 100%)",
              backgroundColor: "#0B1A0F",
            }} />
            {/* pitch lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 140" preserveAspectRatio="none">
              <rect x="2" y="2" width="96" height="136" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
              <line x1="2" y1="70" x2="98" y2="70" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />
              <circle cx="50" cy="70" r="10" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />
              <rect x="20" y="2" width="60" height="14" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />
              <rect x="35" y="2" width="30" height="6" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />
              <rect x="20" y="124" width="60" height="14" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />
              <rect x="35" y="132" width="30" height="6" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />
            </svg>

            {players.map((p, idx) => (
              <button
                key={`${p.id}-${idx}`}
                onClick={() => setSelected(idx)}
                data-testid={`pitch-player-${p.id}`}
                className={`absolute -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full border-2 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.1em] transition-all ${
                  selected === idx
                    ? "border-magenta bg-magenta/20 text-white scale-110 shadow-glow-magenta"
                    : "border-primary bg-primary/10 text-primary hover:scale-110 hover:bg-primary/20"
                }`}
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
              >
                {p.id}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {selected !== null && players[selected] && (
            <div className="card-solid p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient mb-2">{players[selected].id}</div>
              <label className="label">Spielername</label>
              <input
                className="input"
                value={players[selected].name || ""}
                onChange={(e) => updatePlayer(selected, { name: e.target.value })}
                placeholder="Mbappé, Bellingham, …"
                data-testid="player-name-input"
              />
              <div className="mt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Player Instructions</div>
                <div className="flex flex-wrap gap-1.5">
                  {(PLAYER_INSTRUCTIONS_PER_ROLE[players[selected].role] || []).map((pi) => {
                    const active = (players[selected].instructions || []).includes(pi);
                    return (
                      <button
                        key={pi}
                        onClick={() => togglePlayerInstruction(selected, pi)}
                        data-testid={`pi-${pi}`}
                        className={`text-[10px] uppercase tracking-[0.2em] font-mono px-2 py-1 border rounded-sm transition-colors ${
                          active ? "border-primary bg-primary/10 text-primary" : "border-white/10 text-white/60 hover:border-primary/40"
                        }`}
                      >
                        {pi}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="card-solid p-4 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Team Instructions</div>
            {TEAM_INSTRUCTIONS.map((ti) => (
              <div key={ti.k}>
                <label className="label">{ti.l}</label>
                <select
                  className="input"
                  value={instructions[ti.k] || ""}
                  onChange={(e) => setInstructions({ ...instructions, [ti.k]: e.target.value })}
                  data-testid={`ti-${ti.k}`}
                >
                  <option value="">— wählen —</option>
                  {ti.opts.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div className="card-solid p-4 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Set Pieces</div>
            {[
              ["corners_taker", "Eckbälle"],
              ["free_kicks_taker", "Freistöße"],
              ["penalty_taker", "Elfmeter"],
            ].map(([k, l]) => (
              <div key={k}>
                <label className="label">{l}</label>
                <input className="input" value={setPieces[k] || ""} onChange={(e) => setSetPieces({ ...setPieces, [k]: e.target.value })} data-testid={`sp-${k}`} />
              </div>
            ))}
          </div>

          <label className="card-solid p-3 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="tactic-public-toggle" />
            <Eye className="w-4 h-4" />
            <span className="text-sm">Öffentlich (andere Member können sehen)</span>
          </label>

          {error && <div className="text-red-400 text-sm font-mono">{error}</div>}

          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="btn-primary flex-1" data-testid="tactic-save-btn">
              <Save className="w-4 h-4" /> {saving ? "Speichern…" : "Speichern"}
            </button>
            {!isNew && (
              <button onClick={remove} className="btn-secondary text-red-400" data-testid="tactic-delete-btn">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
